import React, { useEffect, useState, useCallback, createContext } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { Unbounded } from 'next/font/google';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import CookieBanner from '../components/CookieBanner.jsx';
import CartModal from '../components/CartModal';
import TermsPopup from '../components/TermsPopup';
import useUserStore from '../store/userStore';
import MakeaLinkPopup from '../components/MakeaLinkPopup.jsx';
import AuthInitializer from '../components/AuthInitializer';
import { useServerAuth } from '../hooks/useServerAuth'; // Импортируем новый хук
import { 
  hasConsent, 
  subscribeToConsentChanges, 
  hasUserMadeChoice 
} from '../utils/cookieConsentUtil';

const unbounded = Unbounded({
  subsets: ['latin', 'cyrillic'],
  display: 'swap',
  weight: ['300', '400', '500', '700'],
  variable: '--font-unbounded',
});

export const TermsContext = createContext(null);

function BaseLayout({
  title,
  description = '',
  showHeader = true,
  canonicalUrl,
  searchQuery = '',
  mediaType = '',
  children,
  isLoginPage = false,
  // Добавляем серверные данные авторизации
  serverAuthState = null
}) {
  const [currentUrl, setCurrentUrl] = useState(canonicalUrl || '');
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [onAcceptCallback, setOnAcceptCallback] = useState(null);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [gtmLoaded, setGtmLoaded] = useState(false);

  // Синхронизируем серверные данные авторизации с клиентским хранилищем
  const { isAuthenticated, userData } = useServerAuth(
    serverAuthState?.isAuthenticated, 
    serverAuthState?.user
  );

  const { 
    termsAccepted,
    acceptTerms,
    isLoggedIn,
    user
  } = useUserStore();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const currentAnalyticsEnabled = hasConsent('analytics') && hasUserMadeChoice();
    setAnalyticsEnabled(currentAnalyticsEnabled);
    
    const unsubscribe = subscribeToConsentChanges((newPreferences) => {
      setAnalyticsEnabled(newPreferences.analytics);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!canonicalUrl && typeof window !== 'undefined') {
      setCurrentUrl(window.location.href);
    }
  }, [canonicalUrl]);

  const handleClose = () => {
    setIsTermsOpen(false);
  };

  const handleAccept = async () => {
    try {
      acceptTerms();
      
      if (isLoggedIn && user) {
        await fetch(`${process.env.NEXT_PUBLIC_BACK_URL}/terms/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Error accepting terms:', error);
    }

    setIsTermsOpen(false);
    if (onAcceptCallback) {
      onAcceptCallback();
      setOnAcceptCallback(null);
    }
  };

  const requestTerms = useCallback((callback) => {
    setOnAcceptCallback(() => callback);
    setIsTermsOpen(true);
  }, []);

  const handleGTMLoad = () => {
    setGtmLoaded(true);
  };

  return (
    <div className={`flex flex-col ${unbounded.variable} ${isLoginPage ? 'h-screen' : 'min-h-screen'}`}>
      <AuthInitializer />
      
      <Head>
        <meta charSet="UTF-8" />
        <title>{title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" href="/favicon.svg" type="image/svg+xml" />
        {description && <meta name="description" content={description} />}
        
        <link
          rel="preconnect"
          href="https://fonts.googleapis.com"
          crossOrigin="anonymous"
        />
        <link 
          rel="preconnect" 
          href="https://fonts.gstatic.com" 
          crossOrigin="anonymous" 
        />
      </Head>

      {analyticsEnabled && !gtmLoaded && (
        <Script 
          id="google-tag-manager" 
          strategy="afterInteractive"
          onLoad={handleGTMLoad}
        >
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-KC5G5WLD');
          `}
        </Script>
      )}

      {analyticsEnabled && (
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-KC5G5WLD"
            height="0"
            width="0"
            className="hidden invisible"
          ></iframe>
        </noscript>
      )}

      {showHeader && (
        <>
          <TermsPopup isOpen={isTermsOpen} onClose={handleClose} onAccept={handleAccept} />
          <MakeaLinkPopup />
          {/* Передаем данные авторизации в хедер */}
          <Header searchQuery={searchQuery} mediaType={mediaType} serverAuthState={serverAuthState} />
        </>
      )}

      <TermsContext.Provider value={{ requestTerms }}>
        <main className={`flex-grow flex flex-col ${isLoginPage ? 'h-full' : ''} ${showHeader ? 'pt-20 md:pt-0' : ''}`}>
          {children}
        </main>
      </TermsContext.Provider>

      <Footer />
      <CookieBanner />
    </div>
  );
}

export default BaseLayout;